import React, { useEffect, useState } from 'react';
import { Button, Form, Input } from 'reactstrap';
import { changeState, setPicked, setComments, setRemarks } from '../../websocket';
import Swipe from '../Swipe';

const BACKGROUND = {
	NONE: 'bg-light',
	SUCCESS: 'bg-success',
	FAILED: 'bg-danger'
};
const THRESHOLD = {
	x: 200,
	y: 50
};
const ORDER = ['NONE', 'SUCCESS', 'FAILED'];

function LineHeader({ location, item, order, quantity, status }) {
	const itemCode = <>
		{order.interesting && order.interesting.includes(item.code) ? <><i className="fas fa-bullhorn" title="Aandacht vereist"></i>{' '}</> : null}
		{item.code[0]}
		<span className="relevant-number">
			{item.code.slice(1)}
		</span>
	</>;
	if(!order.dropship || status === 'NONE') {
		return <div className="row">
			<h2 className="col-6 col-md-4">{location}</h2>
			<h2 className="col-6 col-md-4 text-right text-md-center">{itemCode}</h2>
			<div className="col-12 col-md-4 text-right">pick <h2 className="d-inline">{quantity}</h2></div>
		</div>;
	}
	return <div className="row">
		<h2 className="col-6 col-md-3">{location}</h2>
		<h2 className="col-6 col-md-3 text-right text-md-center">{itemCode}</h2>
		<h2 className="col-6 col-md-3 text-md-center">{order.customer.name}</h2>
		<div className="col-6 col-md-3 text-right">pick <h2 className="d-inline">{quantity}</h2></div>
	</div>;
}

function Line({ location, line, role, order, total, showRemarks, setShowRemarks, bomFailed }) {
	const { item, quantity, status, blocked, comments } = line;
	const remarks = line.remarks ?? line.wmsRemark;
	const locations = Object.keys(line.locations).length;
	let className = BACKGROUND[status];
	if(bomFailed && status !== 'FAILED') {
		className = 'bg-warning';
	}
	return <>
		{role !== 'PACKAGER' && <LineHeader location={role !== 'CUSTOMER_SERVICE' ? location : <>
			{location}{' '}
			<Button color="primary" size="sm" onClick={e => {
				e.preventDefault();
				setShowRemarks(true);
			}} title="Opmerkingen" disabled={showRemarks}><i className="fas fa-comment"></i></Button>
		</>} item={item} order={order} quantity={quantity} status={status} />}
		<div className={className}>
			<div className="row">
				<div className="col-3 col-md-3 col-lg-1">
					<img alt="" src={item.image} />
				</div>
				<div className="col-9 col-md-3 col-lg-3">
					{item.description}
				</div>
				<div className="col-12 col-md-6 col-lg-6">
					<div className="row">
						<div className="col-6">
							{item.length} &times; {item.width} &times; {item.height} cm<br />
							{blocked && <>Geblokkeerd<br /></>}
							{comments && <>{comments}<br /></>}
							{remarks && <u className="font-weight-bold text-danger">{remarks}</u>}
						</div>
						<div className="col-6">
							{role !== 'PACKAGER' ? <>
								Voorraad: {total}<br />
								{locations > 1 ? `Andere locaties: ${locations}` : null}
							</> : <>
								{item.code[0]}
								<span className="relevant-number">
									{item.code.slice(1)}
								</span><br />
								{quantity}
							</>}
						</div>
					</div>
				</div>
			</div>
		</div>
	</>;
}

function changeStatusListener(location, order, line) {
	return ({ x, y }) => {
		if(location === 'Wordt verplaatst') {
			return;
		}
		const aX = Math.abs(x);
		const aY = Math.abs(y);
		if(aX < THRESHOLD.x || aY > aX || aY > THRESHOLD.y) {
			return;
		}
		if(order.status !== 'NONE' || !order.pickers.length) {
			return;
		}
		const index = (ORDER.length + ORDER.indexOf(line.status) + Math.sign(x)) % ORDER.length;
		changeState(order._id, line._id, location, ORDER[index]);
	};
}

function EditRemarks({ line, submit }) {
	const [value, setValue] = useState(line.remarks ?? line.wmsRemark ?? '');
	return <Form className="d-flex" onSubmit={e => {
		e.preventDefault();
		submit(value);
	}}>
		<Input type="text" value={value} onChange={e => setValue(e.target.value)} autoFocus />
		<input type="submit" className="btn btn-primary" value="Save" />
		<Button color="danger" onClick={e => {
			e.preventDefault();
			submit(line.remarks ?? line.wmsRemark);
		}}>Cancel</Button>
	</Form>;
}

export default function OrderLine({ place, line, role, order, location, quantity: total, bomFailed }) {
	const { quantity, status, comments } = line;
	const [value, setValue] = useState(line.picked || '');
	const [showRemarks, setShowRemarks] = useState(false);
	useEffect(() => {
		if(line.picked !== undefined && +value !== line.picked) {
			setValue(line.picked);
		}
	}, [line]);
	if(bomFailed && line.status === 'NONE' && role !== 'CUSTOMER_SERVICE') {
		return null;
	}
	const lineElem = <Line location={location} line={line} role={role} order={order} total={total} showRemarks={showRemarks} setShowRemarks={setShowRemarks} bomFailed={bomFailed} />;
	return <>
		{role !== 'PACKAGER' ? <Swipe onSwipe={changeStatusListener(place, order, line)}>{lineElem}</Swipe> : <div>{lineElem}</div>}
		{showRemarks && <EditRemarks line={line} submit={remarks => {
			const current = line.remarks ?? line.wmsRemark;
			if(typeof remarks === 'string' && current !== remarks) {
				setRemarks(order._id, line._id, place, remarks);
			}
			setShowRemarks(false);
		}} />}
		{status === 'FAILED' && <div>
			Picked:
			<input
				type="number"
				className="form-control"
				min={0} max={quantity}
				value={value}
				disabled={role === 'PACKAGER'}
				onChange={e => {
					setValue(e.target.value);
					const picked = +e.target.value;
					if(!isNaN(picked) && picked >= 0) {
						setPicked(order._id, line._id, place, Math.min(picked, line.quantity));
					}
				}} />
			{role === 'PICKER' && <>
				Comments:
				<input
					type="text"
					className="form-control"
					value={comments}
					disabled={role === 'PACKAGER'}
					onChange={e => {
						setComments(order._id, line._id, place, e.target.value);
					}} />
			</>}
		</div>}
	</>;
}
